"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Tour = void 0;
const react_1 = require("@thesisedu/react");
const web_1 = require("@thesisedu/web");
const react_2 = __importDefault(require("react"));
const react_dom_1 = require("react-dom");
const react_router_dom_1 = require("react-router-dom");
const styled_components_1 = require("styled-components");
const Context_1 = require("./Context");
const Mask_1 = __importDefault(require("./reactour/Mask"));
const StepFooter_1 = require("./step/StepFooter");
const replaceWithParams_1 = require("./step/replaceWithParams");
const useActiveTour_1 = require("./useActiveTour");
const POPOVER_TARGET_ZINDEX = 100001;
function useSelectorHighlight(selector) {
    const [elementCoords, setElementCoords] = react_2.default.useState(null);
    const [insidePopover, setInsidePopover] = react_2.default.useState(false);
    react_2.default.useEffect(() => {
        if (selector) {
            const element = document.querySelector(selector);
            if (element) {
                const isPopover = !!element.closest('.ant-popover-content');
                setInsidePopover(isPopover);
                element.scrollIntoView({ behavior: 'auto', block: 'center', inline: 'center' });
                let handle = null;
                const observer = new ResizeObserver(() => {
                    cancelAnimationFrame(handle);
                    handle = requestAnimationFrame(() => {
                        setElementCoords(element.getBoundingClientRect().toJSON());
                    });
                });
                observer.observe(element);
                requestAnimationFrame(() => {
                    setElementCoords(element.getBoundingClientRect().toJSON());
                });
                return () => {
                    observer.disconnect();
                };
            }
            else {
                setElementCoords(null);
            }
        }
        else {
            setElementCoords(null);
        }
    }, [selector]);
    return { insidePopover, elementCoords };
}
function useTourContainer() {
    const [container, setContainer] = react_2.default.useState(null);
    react_2.default.useEffect(() => {
        const element = document.createElement('div');
        element.classList.add('react-tour-container');
        document.body.appendChild(element);
        setContainer(element);
        return () => {
            document.body.removeChild(element);
            setContainer(null);
        };
    }, []);
    return container;
}
function Tour({ tour, onFinish }) {
    var _a;
    const [_stepIndex, setStepIndex] = react_2.default.useState(0);
    const stepIndex = Math.min(_stepIndex, tour.steps.length - 1);
    react_2.default.useEffect(() => {
        setStepIndex(0);
    }, [tour]);
    const container = useTourContainer();
    const navigate = (0, react_1.useNavigate)();
    const activeStep = tour.steps[stepIndex];
    const { insidePopover, elementCoords } = useSelectorHighlight((_a = activeStep === null || activeStep === void 0 ? void 0 : activeStep.element) === null || _a === void 0 ? void 0 : _a.selector);
    (0, useActiveTour_1.useActiveTour)(activeStep ? tour : undefined);
    // Prevent closing popovers and modals.
    react_2.default.useEffect(() => {
        if (insidePopover) {
            const handler = (e) => {
                e.stopPropagation();
                e.preventDefault();
                e.stopImmediatePropagation();
            };
            const keyHandler = (e) => {
                if (e.key.toLowerCase() === 'escape') {
                    e.stopPropagation();
                    e.preventDefault();
                    e.stopImmediatePropagation();
                }
            };
            document.addEventListener('mousedown', handler, true);
            document.addEventListener('keyup', keyHandler, true);
            document.addEventListener('keydown', keyHandler, true);
            document.addEventListener('keypress', keyHandler, true);
            return () => {
                document.removeEventListener('mousedown', handler, true);
                document.removeEventListener('keyup', keyHandler, true);
                document.removeEventListener('keydown', keyHandler, true);
                document.removeEventListener('keypress', keyHandler, true);
            };
        }
    }, [insidePopover]);
    // Navigate whenever the active step changes, if it has a navigation step.
    const params = (0, react_router_dom_1.useParams)();
    react_2.default.useEffect(() => {
        if (activeStep === null || activeStep === void 0 ? void 0 : activeStep.navigateTo) {
            navigate((0, replaceWithParams_1.replaceWithParams)(activeStep.navigateTo, params));
        }
    }, [activeStep === null || activeStep === void 0 ? void 0 : activeStep.navigateTo]);
    return container && activeStep
        ? (0, react_dom_1.createPortal)(react_2.default.createElement(Context_1.TourContext.Provider, { value: { currentStepIndex: stepIndex, steps: tour.steps } },
            react_2.default.createElement("div", null,
                react_2.default.createElement(TourVisibleGlobalStyles, null),
                elementCoords ? (react_2.default.createElement(StyledMask, { sizes: elementCoords, highlightedAreaClassName: 'react-tour-highlight', zIndex: insidePopover ? POPOVER_TARGET_ZINDEX : undefined, onClick: e => {
                        var _a;
                        e.preventDefault();
                        e.stopPropagation();
                        const targetElement = e.target;
                        const highlightElement = (_a = targetElement.parentElement) === null || _a === void 0 ? void 0 : _a.querySelector('.react-tour-highlight');
                        if (highlightElement) {
                            (0, web_1.callAttention)(highlightElement);
                        }
                    } })) : null,
                react_2.default.createElement(TipContainer, { key: stepIndex },
                    react_2.default.createElement(TipContentContainer, null,
                        react_2.default.createElement(react_1.Markdown, { children: activeStep.message })),
                    react_2.default.createElement(StepFooter_1.StepFooter, { onUpdateStep: (delta) => __awaiter(this, void 0, void 0, function* () {
                            const delay = activeStep.postDelay || 0.5;
                            if (delay && delta > 0) {
                                yield new Promise(resolve => setTimeout(resolve, delay * 1000));
                            }
                            setStepIndex(current => {
                                const nextStep = current + delta;
                                if (nextStep > tour.steps.length - 1 && onFinish) {
                                    onFinish();
                                    return current;
                                }
                                else {
                                    return Math.max(0, Math.min(current + delta, tour.steps.length - 1));
                                }
                            });
                        }), step: activeStep })))), container)
        : null;
}
exports.Tour = Tour;
const TipContainer = react_1.styled.div `
  box-shadow: ${props => props.theme['@shadow-medium']};
  background: ${props => props.theme['@gray-0']};
  border-radius: ${props => props.theme['@border-radius-base']};
  width: 400px;
  max-width: 50vw;
  max-height: 400px;
  position: fixed;
  bottom: ${props => props.theme['@size-s']};
  right: ${props => props.theme['@size-s']};
  z-index: 1000000;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  overflow: hidden;
`;
const TipContentContainer = react_1.styled.div `
  flex: 1;
  padding: ${props => props.theme['@size-s']};
  overflow-y: auto;
  > :last-child {
    margin-bottom: 0 !important;
  }
`;
const StyledMask = (0, react_1.styled)(Mask_1.default) `
  ${props => (props.zIndex ? `z-index: ${props.zIndex} !important;` : '')}
  > svg {
    ${props => (0, web_1.LightOverlayStyle)(props.theme)}
    color: ${props => props.theme['@gray-3']} !important;
    background: none !important;
    .react-tour-highlight {
      display: block !important;
      pointer-events: none !important;
    }
  }
`;
const TourVisibleGlobalStyles = (0, styled_components_1.createGlobalStyle) `
  body, html {
    overflow: hidden;
  }
  .ant-select-dropdown, .ant-popover {
    z-index: 100000;
  }
`;
//# sourceMappingURL=Tour.js.map